import type { StreamEvent } from "@/types/sync.types";

export type MockSyncScenario =
  | "happy_path"
  | "skipped"
  | "quota_reached_pre"
  | "quota_reached_mid"
  | "max_pages_warning";

export type MockSyncOptions = {
  scenario?: MockSyncScenario;
  pages?: number; // how many sync.page.fetched events
  perPage?: number; // activities per page
  delayMs?: number; // delay between events
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted)
      return reject(new DOMException("Aborted", "AbortError"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

function ndjsonLine(evt: StreamEvent) {
  return JSON.stringify(evt) + "\n";
}

export function createMockNdjsonResponse(
  opts: MockSyncOptions = {},
  signal?: AbortSignal,
): Response {
  const scenario = opts.scenario ?? "happy_path";
  const pages = Math.max(1, opts.pages ?? 5);
  const perPage = Math.max(1, opts.perPage ?? 200);
  const delayMs = Math.max(0, opts.delayMs ?? 200);

  const encoder = new TextEncoder();

  const stream = new ReadableStream<Uint8Array>({
    start(controller) {
      // If the consumer aborts, close the stream.
      if (signal) {
        signal.addEventListener(
          "abort",
          () => {
            try {
              controller.error(new DOMException("Aborted", "AbortError"));
            } catch {
              // ignore
            }
          },
          { once: true },
        );
      }

      (async () => {
        const send = async (evt: StreamEvent, delay = delayMs) => {
          if (delay > 0) await sleep(delay, signal);
          controller.enqueue(encoder.encode(ndjsonLine(evt)));
        };

        // Common start
        await send({ type: "start" } as StreamEvent, 0);

        if (scenario === "skipped") {
          await send({
            type: "skipped",
            details: {
              age_minutes: 12,
              last_synced_at: new Date(Date.now() - 12 * 60_000).toISOString(),
            },
          } as StreamEvent);
          await send({ type: "done", ok: true } as StreamEvent);
          controller.close();
          return;
        }

        if (scenario === "quota_reached_pre") {
          await send({
            type: "warning",
            code: "sync.quota.reached",
            details: { limit: 20, phase: "pre" },
          } as StreamEvent);
          await send({ type: "done", ok: false } as StreamEvent);
          controller.close();
          return;
        }

        await send({
          type: "info",
          code: "sync.lock.acquired",
          details: { lock_minutes: 3 },
        } as StreamEvent);

        await send({
          type: "info",
          code: "sync.token_refresh.start",
        } as StreamEvent);
        await send({
          type: "info",
          code: "sync.token_refresh.done",
        } as StreamEvent);

        await send({
          type: "info",
          code: "sync.cutoff.set",
          details: {
            cutoff_iso: new Date(Date.now() - 30 * 86400_000).toISOString(),
            overlap_days: 30,
          },
        } as StreamEvent);

        let activitiesFetched = 0;

        for (let page = 1; page <= pages; page++) {
          if (scenario === "quota_reached_mid" && page === Math.min(3, pages)) {
            await send({
              type: "warning",
              code: "sync.quota.reached",
              details: { limit: 20, phase: "mid" },
            } as StreamEvent);
            await send({ type: "done", ok: false } as StreamEvent);
            controller.close();
            return;
          }

          activitiesFetched += perPage;

          await send({
            type: "info",
            code: "sync.page.fetched",
            details: {
              page,
              page_items: perPage,
              activities_fetched: activitiesFetched,
              pages_fetched: page,
            },
          } as StreamEvent);
        }

        await send({
          type: "info",
          code: "sync.fetch.done",
          details: {
            pages_fetched: pages,
            activities_fetched: activitiesFetched,
          },
        } as StreamEvent);

        if (scenario === "max_pages_warning") {
          await send({
            type: "warning",
            code: "sync.warning.max_pages",
            details: { max_pages: pages },
          } as StreamEvent);
        }

        await send({ type: "info", code: "sync.persist.start" } as StreamEvent);
        await send({
          type: "info",
          code: "sync.persist.upsert_done",
          details: { upserted: activitiesFetched },
        } as StreamEvent);

        await send({
          type: "done",
          ok: true,
          warning_codes:
            scenario === "max_pages_warning" ? ["sync.warning.max_pages"] : [],
          details: {
            last_synced_at: new Date(Date.now()).toISOString(),
            pages_fetched: pages,
            activities_fetched: activitiesFetched,
            upserted: activitiesFetched,
            marked_deleted: 0,
            cutoff_iso: new Date(Date.now() - 30 * 86400_000).toISOString(),
            backfill_done: true,
            newest_start_date: new Date().toISOString(),
            oldest_start_date: new Date(
              Date.now() - 365 * 86400_000,
            ).toISOString(),
          },
        } as StreamEvent);

        controller.close();
      })().catch((e) => {
        // AbortError should just error the stream.
        controller.error(e);
      });
    },
  });

  return new Response(stream, {
    status: 200,
    headers: {
      "content-type": "application/x-ndjson",
      "cache-control": "no-store",
    },
  });
}
